import { useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { globalStyles, homeStyles } from "../styles";
import logo from "../logo.png";
import { Helmet } from "react-helmet-async";

const CUISINE_USAGE_STORAGE_KEY = "nextbiteCuisineUsage";
const CUISINE_OPTIONS = ["Indian", "Italian", "Chinese", "Mexican", "Japanese", "Other"];

/* ---------------- COMPONENT ---------------- */

export default function Home() {
  const nav = useNavigate();
  const builderRef = useRef(null);

  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [otherCuisine, setOtherCuisine] = useState("");
  const [location, setLocation] = useState("");
  const [concerns, setConcerns] = useState("");
  const [texture, setTexture] = useState("");
  const [otherTexture, setOtherTexture] = useState("");
  const [loading, setLoading] = useState(false);

  const [result, setResult] = useState(null);
  const [step, setStep] = useState(1);
  const [message, setMessage] = useState("");

  const [preferences, setPreferences] = useState("");
  const [allergies, setAllergies] = useState([]);
  const [allergyInput, setAllergyInput] = useState("");
  const [locationError, setLocationError] = useState("");

  const textures = ["Soft", "Crispy", "Chewy", "Crunchy", "Other"];
  const allergyOptions = [
    "Dairy",
    "Nuts",
    "Gluten",
    "Soy",
    "Egg"
  ];

  const cuisines = useMemo(() => {
    let usage = {};

    try {
      usage = JSON.parse(localStorage.getItem(CUISINE_USAGE_STORAGE_KEY) || "{}");
    } catch {
      usage = {};
    }

    return [...CUISINE_OPTIONS].sort((a, b) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;

      return (usage[b] || 0) - (usage[a] || 0);
    });
  }, []);

  const addAllergy = useCallback((value) => {
    const cleanedValue = value.trim();

    if (!cleanedValue) return;

    setAllergies((current) => {
      const alreadySelected = current.some(
        (item) => item.toLowerCase() === cleanedValue.toLowerCase()
      );

      if (alreadySelected) {
        return current;
      }

      return [...current, cleanedValue];
    });

    setAllergyInput("");
  }, []);

  const removeAllergy = useCallback((value) => {
    setAllergies((current) => current.filter((item) => item !== value));
  }, []);

  const toggleCuisine = useCallback((value) => {
    setSelectedCuisines((current) => {
      if (current.includes(value)) {
        if (value === "Other") {
          setOtherCuisine("");
        }
        return current.filter((item) => item !== value);
      }

      return [...current, value];
    });
  }, []);


  const validateLocation = useCallback((value) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return "Enter a city, town, region, or country.";
    }

    if (trimmedValue.length < 2) {
      return "Location must be at least 2 characters.";
    }

    if (trimmedValue.length > 80) {
      return "Location is too long. Keep it under 80 characters.";
    }

    if (!/[\p{L}\p{M}]/u.test(trimmedValue)) {
      return "Location should include at least one letter.";
    }

    if (!/^[\p{L}\p{M}\d\s,.'-]+$/u.test(trimmedValue)) {
      return "Use letters, numbers, spaces, commas, apostrophes, periods, or hyphens only.";
    }

    return "";
  }, []);

  /* ---------------- GENERATE ---------------- */

  const handleGenerate = useCallback(async () => {
    const trimmedLocation = location.trim();
    const nextLocationError = trimmedLocation ? validateLocation(trimmedLocation) : "";

    if (nextLocationError) {
      setLocationError(nextLocationError);
      return;
    }

    setLoading(true);

    try {
      const chosenCuisines = selectedCuisines.map((item) =>
        item === "Other" ? otherCuisine.trim() || "Other" : item
      );
      const chosenTexture = texture === "Other" ? otherTexture.trim() || "Other" : texture;

      const resultState = {
        cuisine: chosenCuisines.join(", "),
        cuisines: chosenCuisines,
        location: trimmedLocation,
        concerns,
        texture: chosenTexture,
        preferences,
        allergies
      };

      // Simulate API call delay for better UX
      await new Promise(resolve => setTimeout(resolve, 500));

      try {
        const currentUsage = JSON.parse(
          localStorage.getItem(CUISINE_USAGE_STORAGE_KEY) || "{}"
        );
        chosenCuisines.forEach((item) => {
          currentUsage[item] = (currentUsage[item] || 0) + 1;
        });
        localStorage.setItem(CUISINE_USAGE_STORAGE_KEY, JSON.stringify(currentUsage));
      } catch {
        // ignore storage errors
      }

      localStorage.setItem("nextbiteResultState", JSON.stringify(resultState));
      nav("/home/result", { state: resultState });
    } catch (error) {
      console.error("Error generating suggestions:", error);
      setMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedCuisines, otherCuisine, location, concerns, texture, otherTexture, preferences, allergies, nav, validateLocation]);

  const reset = useCallback(() => {
    setSelectedCuisines([]);
    setOtherCuisine("");
    setLocation("");
    setConcerns("");
    setTexture("");
    setOtherTexture("");
    setPreferences("");
    setAllergies([]);
    setAllergyInput("");
    setLocationError("");
    setResult(null);
    setStep(1);
    setMessage("");
  }, []);

  const handleTryNextBite = useCallback(() => {
    builderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div style={homeStyles.page}>
      <Helmet>
        <title>NextBite — Discover meal ideas</title>
        <meta name="description" content="Get smart, personalised meal suggestions for your child based on what they just ate. No more decision fatigue." />
        <meta property="og:title" content="NextBite — What should my child eat next?" />
        <meta property="og:url" content="https://www.nextbite.ca/home" />
        <meta property="og:image" content="https://www.nextbite.ca/og-image.png" />
      </Helmet>
      {/* HERO SECTION */}
      <section style={homeStyles.hero}>
        <div style={homeStyles.heroCopy}>
          <img src={logo} alt="NextBite" style={homeStyles.heroLogo} />
          <h1 style={homeStyles.heroTitle}>
            Are you a busy parent with young kids?
          </h1>
          <p style={homeStyles.heroSubtitle}>
            NextBite helps you decide what to feed next — without the daily
            stress and decision fatigue.
          </p>
          <button
            type="button"
            onClick={handleTryNextBite}
            style={homeStyles.heroButton}
          >
            Try NextBite
          </button>
        </div>
      </section>

      {/* BUILDER */}
      <div style={{ padding: "0 20px" }}>
      <div ref={builderRef} style={formStyles.shell}>

        {/* Cuisine */}
        <div style={formStyles.section}>
          <div style={formStyles.labelRow}>
            <span style={formStyles.labelText}>Cuisine</span>
            <span style={formStyles.labelBadge}>pick one or more</span>
          </div>
          <div style={formStyles.chipRow}>
            {cuisines.map((c, i) => {
              const active = selectedCuisines.includes(c);
              return (
                <button key={c} onClick={() => toggleCuisine(c)} style={{
                  ...formStyles.chip,
                  ...(active ? formStyles.chipActive : formStyles.chipIdle),
                  animation: `chipFloat ${2.8 + i * 0.25}s ease-in-out infinite`,
                  animationDelay: `${i * 0.3}s`
                }}>
                  {c}
                </button>
              );
            })}
          </div>
          {selectedCuisines.includes("Other") && (
            <textarea rows={2} placeholder="e.g. Thai, Middle Eastern, comfort food…"
              value={otherCuisine} onChange={(e) => setOtherCuisine(e.target.value)}
              style={formStyles.textarea} />
          )}
        </div>

        <div style={formStyles.divider} />

        {/* Location */}
        <div style={formStyles.section}>
          <div style={formStyles.labelRow}>
            <span style={formStyles.labelText}>Location</span>
            <span style={formStyles.labelBadge}>optional</span>
          </div>
          <input placeholder="City, region, or country" value={location}
            onChange={(e) => { setLocation(e.target.value); if (locationError) setLocationError(""); }}
            style={formStyles.input} />
          {locationError && <p style={formStyles.error}>{locationError}</p>}
        </div>

        <div style={formStyles.divider} />

        {/* Concerns */}
        <div style={formStyles.section}>
          <div style={formStyles.labelRow}>
            <span style={formStyles.labelText}>Concerns</span>
            <span style={formStyles.labelBadge}>optional</span>
          </div>
          <textarea rows={3} placeholder="e.g. Doesn't like spicy food, prefers mild flavors…"
            value={concerns} onChange={(e) => setConcerns(e.target.value)}
            style={formStyles.textarea} />
        </div>

        <div style={formStyles.divider} />

        {/* Allergies */}
        <div style={formStyles.section}>
          <div style={formStyles.labelRow}>
            <span style={formStyles.labelText}>Allergies</span>
            <span style={formStyles.labelBadge}>optional</span>
          </div>
          <div style={formStyles.chipRow}>
            {allergyOptions.map((option, i) => {
              const active = allergies.some((item) => item.toLowerCase() === option.toLowerCase());
              return (
                <button key={option}
                  onClick={() => active ? removeAllergy(option) : addAllergy(option)}
                  style={{
                    ...formStyles.chip,
                    ...(active ? formStyles.chipActive : formStyles.chipIdle),
                    animation: `chipFloat ${2.6 + i * 0.3}s ease-in-out infinite`,
                    animationDelay: `${i * 0.35}s`
                  }}>
                  {option}
                </button>
              );
            })}
          </div>
          <div style={formStyles.addRow}>
            <input placeholder="Add another…" value={allergyInput}
              onChange={(e) => setAllergyInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAllergy(allergyInput); } }}
              list="allergy-options"
              style={{ ...formStyles.input, marginTop: 0 }} />
            <button type="button" onClick={() => addAllergy(allergyInput)} style={formStyles.addBtn}>+ Add</button>
          </div>
          <datalist id="allergy-options">{allergyOptions.map((o) => <option key={o} value={o} />)}</datalist>
          {allergies.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              {allergies.map((a) => (
                <button key={a} type="button" onClick={() => removeAllergy(a)} style={formStyles.tagChip}>
                  {a} ×
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={formStyles.divider} />

        {/* Texture */}
        <div style={formStyles.section}>
          <div style={formStyles.labelRow}>
            <span style={formStyles.labelText}>Texture</span>
            <span style={formStyles.labelBadge}>optional</span>
          </div>
          <div style={formStyles.chipRow}>
            {textures.map((t, i) => (
              <button key={t}
                onClick={() => { setTexture(t); if (t !== "Other") setOtherTexture(""); }}
                style={{
                  ...formStyles.chip,
                  ...(texture === t ? formStyles.chipActive : formStyles.chipIdle),
                  animation: `chipFloat ${2.5 + i * 0.28}s ease-in-out infinite`,
                  animationDelay: `${i * 0.32}s`
                }}>
                {t}
              </button>
            ))}
          </div>
          {texture === "Other" && (
            <textarea rows={2} placeholder="e.g. soft and creamy, easy to chew…"
              value={otherTexture} onChange={(e) => setOtherTexture(e.target.value)}
              style={formStyles.textarea} />
          )}
        </div>

        {/* Generate */}
        <div style={formStyles.generateWrap}>
          <button onClick={handleGenerate} disabled={loading} style={{
            ...formStyles.generateBtn,
            opacity: loading ? 0.75 : 1,
            cursor: loading ? "not-allowed" : "pointer"
          }}>
            {loading ? "Finding ideas…" : "Get meal ideas →"}
          </button>
        </div>

      </div>
      

      {/* STEP 2 - RESULTS */}
      {step === 3 && result && (
        <div>
          <div style={globalStyles.card}>
            <h3>🍽 Try this next</h3>
            {result.next.map((i) => (
              <p key={i}>{i}</p>
            ))}
          </div>

          <div style={globalStyles.card}>
            <h3>✨ Upgrade options</h3>
            {result.upgrade.map((i) => (
              <p key={i}>{i}</p>
            ))}
          </div>

          <p style={{ marginTop: 10, fontStyle: "italic" }}>
            {message}
          </p>

          <button onClick={reset} style={globalStyles.primaryBtn}>
            Try Again
          </button>
        </div>
      )}

      </div> {/* end padding wrapper */}
    </div>
  );
}

const formStyles = {
  shell: {
    background: "rgba(255,255,255,0.95)",
    boxShadow: "0 -1px 0 rgba(226,232,240,0.8)",
    overflow: "hidden",
    maxWidth: 780,
    margin: "0 auto",
    borderRadius: 24,
    marginTop: 32,
    marginBottom: 40,
    border: "1px solid rgba(226,232,240,0.6)"
  },
  section: {
    padding: "26px 28px"
  },
  divider: {
    height: 1,
    background: "linear-gradient(90deg, transparent, #e2e8f0 20%, #e2e8f0 80%, transparent)",
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16
  },
  labelText: {
    fontSize: 17,
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.01em"
  },
  labelBadge: {
    padding: "3px 10px",
    borderRadius: 999,
    background: "#f1f5f9",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 500,
    marginLeft: 2
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10
  },
  chip: {
    padding: "10px 18px",
    borderRadius: 999,
    border: "2px solid transparent",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    transition: "all 0.2s ease",
    fontFamily: "inherit",
    letterSpacing: "-0.01em"
  },
  chipIdle: {
    background: "#fff",
    color: "#475569",
    border: "2px solid #e2e8f0",
    boxShadow: "0 2px 6px rgba(15,23,42,0.05)"
  },
  chipActive: {
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    color: "#fff",
    border: "2px solid transparent",
    boxShadow: "0 6px 18px rgba(242,95,76,0.32)"
  },
  input: {
    width: "100%",
    padding: "13px 16px",
    borderRadius: 14,
    border: "2px solid #e2e8f0",
    outline: "none",
    fontSize: 15,
    color: "#0f172a",
    background: "#fafbfc",
    fontFamily: "inherit",
    marginTop: 4,
    boxSizing: "border-box",
    transition: "border-color 0.2s ease"
  },
  textarea: {
    width: "100%",
    padding: "13px 16px",
    borderRadius: 14,
    border: "2px solid #e2e8f0",
    outline: "none",
    fontSize: 15,
    color: "#0f172a",
    background: "#fafbfc",
    fontFamily: "inherit",
    resize: "vertical",
    lineHeight: 1.6,
    marginTop: 4,
    boxSizing: "border-box"
  },
  addRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginTop: 14
  },
  addBtn: {
    padding: "13px 20px",
    borderRadius: 14,
    border: "2px solid #e2e8f0",
    background: "#fff",
    color: "#334155",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: "inherit"
  },
  tagChip: {
    padding: "7px 14px",
    borderRadius: 999,
    border: "2px solid #fecdd3",
    background: "linear-gradient(135deg, #fff1f2, #ffe4e6)",
    color: "#be123c",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit"
  },
  error: {
    margin: "8px 0 0",
    fontSize: 13,
    color: "#dc2626"
  },
  generateWrap: {
    padding: "8px 28px 28px"
  },
  generateBtn: {
    display: "block",
    width: "100%",
    padding: "16px 24px",
    borderRadius: 18,
    border: "none",
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    color: "#fff",
    fontSize: 17,
    fontWeight: 700,
    boxShadow: "0 12px 32px rgba(242,95,76,0.32)",
    fontFamily: "inherit",
    letterSpacing: "-0.01em",
    transition: "transform 0.18s ease, box-shadow 0.18s ease"
  }
};
